import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Step 1 of the password reset flow: request a recovery email. We show the same
 * neutral confirmation whether or not the address has an account — Supabase does
 * not disclose account existence, and neither should we.
 */
export function ForgotPasswordPage() {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { error: resetError } = await auth.sendPasswordReset(email);
      if (resetError) {
        setError(resetError);
        return;
      }
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-purple to-brand-purple-light">
      <div className="w-full max-w-md rounded-xl bg-white p-10 shadow-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-brand-purple">PathChron</h1>
          <p className="text-sm text-gray-500">Enterprise Device Management</p>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-brand-black">Check your email</h2>
              <p className="mt-2 text-sm text-gray-500">
                If an account exists for <span className="font-medium">{email}</span>, we&apos;ve
                sent a link to reset your password. Follow it to choose a new one.
              </p>
            </div>
            <Link
              to="/login"
              className="font-medium text-brand-purple hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold text-brand-black">Reset your password</h2>
              <p className="mt-1 text-sm text-gray-400">
                Enter your email and we&apos;ll send you a reset link
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-brand-orange-light p-3 text-sm text-brand-orange">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-black">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-3 rounded-lg bg-brand-purple px-6 py-3 font-medium text-white shadow-sm transition-colors hover:bg-brand-purple/90 disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send reset link'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-500">
              Remembered it?{' '}
              <Link to="/login" className="font-medium text-brand-purple hover:underline">
                Sign in
              </Link>
            </p>
          </>
        )}

        <p className="mt-8 text-center text-xs text-gray-400">
          PathChron — Enterprise Device Management
        </p>
      </div>
    </div>
  );
}
